/**
 * 회원 관리 데모 페이지
 * 실제 구축된 UI를 Mock 데이터로 시연
 */

import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  UserGroupIcon,
  MagnifyingGlassIcon,
  PlusIcon,
  FaceSmileIcon,
  ArrowDownTrayIcon,
  XMarkIcon,
  PencilIcon,
  TrashIcon,
} from '@heroicons/react/24/outline';
import DemoNavigation from '../components/DemoNavigation';
import { getTodayKST } from '../utils/dateUtils';

// Mock 데이터
const MOCK_MEMBERS = [
  {
    id: '1',
    member_id: 'M001',
    user_number: 'M001',
    name: '김철수',
    phone: '010-1234-5678',
    email: 'kim@example.com',
    birth_date: '2000-05-15',
    gender: '남성',
    address: '서울시 강남구',
    parent_phone: '010-9876-5432',
    membership_type: 'monthly',
    membership_status: 'active',
    face_registered: true,
    current_seat: '12',
    last_visit: '2025-01-24T14:30:00',
    created_at: '2025-01-01',
    total_usage_hours: 180,
    remaining_time: 720,
    expiry_date: '2025-02-28',
    join_date: '2025-01-01',
    attendanceHistory: [
      { date: '2025-01-24', entry: '09:00', exit: '18:00', duration: '9시간' },
      { date: '2025-01-23', entry: '10:00', exit: '19:00', duration: '9시간' },
      { date: '2025-01-22', entry: '09:30', exit: '17:30', duration: '8시간' },
    ],
    paymentHistory: [
      { date: '2025-01-01', type: '월정액권', amount: 150000, method: '카드', status: '완료' },
    ],
    counselingHistory: [
      { date: '2025-01-15', counselor: '관리자', content: '학습 상담 진행', category: '학습' },
    ],
    studyAnalysis: {
      totalDays: 24,
      totalHours: 180,
      avgHours: 7.5,
      mostActiveTime: '14:00-18:00',
    },
  },
  {
    id: '2',
    member_id: 'M002',
    user_number: 'M002',
    name: '이영희',
    phone: '010-2345-6789',
    email: 'lee@example.com',
    birth_date: '1999-08-20',
    gender: '여성',
    address: '서울시 서초구',
    parent_phone: '010-8765-4321',
    membership_type: 'daily',
    membership_status: 'active',
    face_registered: true,
    current_seat: '25',
    last_visit: '2025-01-24T16:00:00',
    created_at: '2025-01-05',
    total_usage_hours: 120,
    remaining_time: 480,
    expiry_date: '2025-02-05',
    join_date: '2025-01-05',
    attendanceHistory: [
      { date: '2025-01-24', entry: '13:00', exit: '21:00', duration: '8시간' },
      { date: '2025-01-23', entry: '14:00', exit: '20:00', duration: '6시간' },
    ],
    paymentHistory: [
      { date: '2025-01-05', type: '일일권', amount: 100000, method: '현금', status: '완료' },
    ],
    counselingHistory: [],
    studyAnalysis: {
      totalDays: 20,
      totalHours: 120,
      avgHours: 6.0,
      mostActiveTime: '13:00-21:00',
    },
  },
  {
    id: '3',
    member_id: 'M003',
    user_number: 'M003',
    name: '박민수',
    phone: '010-3456-7890',
    email: 'park@example.com',
    birth_date: '2001-03-10',
    gender: '남성',
    address: '서울시 송파구',
    parent_phone: '010-7654-3210',
    membership_type: 'hourly',
    membership_status: 'active',
    face_registered: false,
    current_seat: null,
    last_visit: '2025-01-23T11:00:00',
    created_at: '2025-01-10',
    total_usage_hours: 60,
    remaining_time: 240,
    expiry_date: '2025-02-10',
    join_date: '2025-01-10',
    attendanceHistory: [
      { date: '2025-01-23', entry: '09:00', exit: '15:00', duration: '6시간' },
    ],
    paymentHistory: [
      { date: '2025-01-10', type: '시간권', amount: 80000, method: '카드', status: '완료' },
    ],
    counselingHistory: [
      { date: '2025-01-12', counselor: '관리자', content: '좌석 배정 상담', category: '시설' },
    ],
    studyAnalysis: {
      totalDays: 15,
      totalHours: 60,
      avgHours: 4.0,
      mostActiveTime: '09:00-15:00',
    },
  },
  {
    id: '4',
    member_id: 'M004',
    user_number: 'M004',
    name: '정수진',
    phone: '010-4567-8901',
    email: 'jung@example.com',
    birth_date: '2002-11-25',
    gender: '여성',
    address: '서울시 강동구',
    parent_phone: '010-6543-2109',
    membership_type: 'monthly',
    membership_status: 'suspended',
    face_registered: true,
    current_seat: null,
    last_visit: '2025-01-20T10:00:00',
    created_at: '2024-12-15',
    total_usage_hours: 200,
    remaining_time: 0,
    expiry_date: '2025-01-15',
    join_date: '2024-12-15',
    attendanceHistory: [
      { date: '2025-01-20', entry: '08:00', exit: '12:00', duration: '4시간' },
    ],
    paymentHistory: [
      { date: '2024-12-15', type: '월정액권', amount: 150000, method: '카드', status: '완료' },
    ],
    counselingHistory: [
      { date: '2025-01-10', counselor: '관리자', content: '이용권 연장 안내', category: '결제' },
    ],
    studyAnalysis: {
      totalDays: 30,
      totalHours: 200,
      avgHours: 6.7,
      mostActiveTime: '08:00-17:00',
    },
  },
  {
    id: '5',
    member_id: 'M005',
    user_number: 'M005',
    name: '최동욱',
    phone: '010-5678-9012',
    email: 'choi@example.com',
    birth_date: '2000-07-08',
    gender: '남성',
    address: '서울시 마포구',
    parent_phone: '010-5432-1098',
    membership_type: 'daily',
    membership_status: 'active',
    face_registered: true,
    current_seat: '8',
    last_visit: '2025-01-24T13:00:00',
    created_at: '2025-01-15',
    total_usage_hours: 72,
    remaining_time: 360,
    expiry_date: '2025-02-15',
    join_date: '2025-01-15',
    attendanceHistory: [
      { date: '2025-01-24', entry: '11:00', exit: '19:00', duration: '8시간' },
      { date: '2025-01-23', entry: '10:00', exit: '18:00', duration: '8시간' },
    ],
    paymentHistory: [
      { date: '2025-01-15', type: '일일권', amount: 100000, method: '카드', status: '완료' },
    ],
    counselingHistory: [],
    studyAnalysis: {
      totalDays: 9,
      totalHours: 72,
      avgHours: 8.0,
      mostActiveTime: '10:00-19:00',
    },
  },
];

const DemoMembersPage: React.FC = () => {
  const navigate = useNavigate();
  const [selectedMember, setSelectedMember] = useState<typeof MOCK_MEMBERS[0] | null>(null);
  const [activeTab, setActiveTab] = useState<'info' | 'attendance' | 'payments' | 'counseling' | 'study'>('info');
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [membershipFilter, setMembershipFilter] = useState('all');
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isEditPhoneModalOpen, setIsEditPhoneModalOpen] = useState(false);
  const [selectedDate, setSelectedDate] = useState(getTodayKST());

  // 필터링된 회원 목록
  const filteredMembers = MOCK_MEMBERS.filter((member) => {
    const matchesSearch =
      member.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      member.phone.includes(searchTerm) ||
      member.user_number.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || member.membership_status === statusFilter;
    const matchesMembership = membershipFilter === 'all' || member.membership_type === membershipFilter;
    return matchesSearch && matchesStatus && matchesMembership;
  });

  // 이용권 뱃지
  const getMembershipBadge = (type: string) => {
    const badges: Record<string, { label: string; className: string }> = {
      monthly: { label: '월정액권', className: 'bg-blue-100 text-blue-800' },
      daily: { label: '일일권', className: 'bg-green-100 text-green-800' },
      hourly: { label: '시간권', className: 'bg-purple-100 text-purple-800' },
    };
    return badges[type] || { label: '없음', className: 'bg-gray-100 text-gray-800' };
  };

  // 상태 뱃지
  const getStatusBadge = (status: string) => {
    const badges: Record<string, { label: string; className: string }> = {
      active: { label: '활성', className: 'bg-green-100 text-green-800' },
      suspended: { label: '정지', className: 'bg-red-100 text-red-800' },
      expired: { label: '만료', className: 'bg-gray-100 text-gray-800' },
    };
    return badges[status] || { label: '알 수 없음', className: 'bg-gray-100 text-gray-800' };
  };

  // 날짜 포맷
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('ko-KR');
  };

  // 시간 포맷
  const formatDateTime = (dateString: string) => {
    return new Date(dateString).toLocaleString('ko-KR');
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* 데모 헤더 */}
      <div className="bg-blue-600 text-white py-3 px-4 text-center">
        <p className="text-sm">
          📢 이것은 데모 페이지입니다. 모든 데이터는 가상 데이터이며, 데모 시연을 위해 주기적으로 업데이트되어 조금씩 변동될 수 있습니다.
        </p>
      </div>

      {/* 네비게이션 헤더 */}
      <DemoNavigation currentTitle="회원 관리 (데모)" currentIcon={UserGroupIcon} />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* 안내 메시지 */}
        <div className="mb-6 bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <p className="text-sm text-yellow-800">
            💡 실제 시스템과 동일한 화면으로 회원을 관리할 수 있습니다!
          </p>
        </div>

        {/* 검색 및 필터 */}
        <div className="bg-white rounded-lg shadow mb-6 p-6">
          <div className="flex flex-col md:flex-row gap-4">
            {/* 검색 */}
            <div className="flex-1">
              <div className="relative">
                <MagnifyingGlassIcon className="h-5 w-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  placeholder="이름, 전화번호, 회원번호로 검색..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
            </div>

            {/* 상태 필터 */}
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="all">전체 상태</option>
              <option value="active">활성</option>
              <option value="suspended">정지</option>
              <option value="expired">만료</option>
            </select>

            {/* 이용권 필터 */}
            <select
              value={membershipFilter}
              onChange={(e) => setMembershipFilter(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="all">전체 이용권</option>
              <option value="monthly">월정액권</option>
              <option value="daily">일일권</option>
              <option value="hourly">시간권</option>
            </select>
          </div>

          {/* 액션 버튼 */}
          <div className="flex gap-2 mt-4">
            <button
              onClick={() => alert('데모 모드에서는 회원 추가가 제한됩니다.')}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2"
            >
              <PlusIcon className="h-5 w-5" />
              회원 추가
            </button>
            <button
              onClick={() => alert('데모 모드에서는 얼굴 등록이 제한됩니다.')}
              className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors flex items-center gap-2"
            >
              <FaceSmileIcon className="h-5 w-5" />
              얼굴 등록
            </button>
            <button
              onClick={() => alert('데모 모드에서는 내보내기가 제한됩니다.')}
              className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors flex items-center gap-2"
            >
              <ArrowDownTrayIcon className="h-5 w-5" />
              내보내기
            </button>
          </div>
        </div>

        {/* 회원 테이블 */}
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  회원 정보
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  이용권
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  상태
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  얼굴 등록
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  현재 좌석
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  최근 방문
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  관리
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredMembers.map((member) => {
                const membershipBadge = getMembershipBadge(member.membership_type);
                const statusBadge = getStatusBadge(member.membership_status);

                return (
                  <tr key={member.id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div>
                          <div className="text-sm font-medium text-gray-900">{member.name}</div>
                          <div className="text-sm text-gray-500">{member.user_number}</div>
                          <div className="text-sm text-gray-500">{member.phone}</div>
                          <div className="text-sm text-gray-500">{member.email}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${membershipBadge.className}`}>
                        {membershipBadge.label}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${statusBadge.className}`}>
                        {statusBadge.label}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                        member.face_registered ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                      }`}>
                        {member.face_registered ? '등록됨' : '미등록'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {member.current_seat || '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {formatDateTime(member.last_visit)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <button
                        onClick={() => {
                          setSelectedMember(member);
                          setActiveTab('info');
                        }}
                        className="text-blue-600 hover:text-blue-900 mr-3"
                      >
                        상세보기
                      </button>
                      {!member.face_registered && (
                        <button
                          onClick={() => alert('데모 모드에서는 얼굴 등록이 제한됩니다.')}
                          className="text-purple-600 hover:text-purple-900"
                        >
                          얼굴등록
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>

          {filteredMembers.length === 0 && (
            <div className="text-center py-12">
              <UserGroupIcon className="mx-auto h-12 w-12 text-gray-400" />
              <h3 className="mt-2 text-sm font-medium text-gray-900">회원 없음</h3>
              <p className="mt-1 text-sm text-gray-500">검색 조건에 맞는 회원이 없습니다.</p>
            </div>
          )}
        </div>

        {/* 결과 개수 */}
        <div className="mt-4 text-sm text-gray-600 text-center">
          총 {filteredMembers.length}명의 회원이 검색되었습니다.
        </div>
      </div>

      {/* 회원 상세 모달 */}
      {selectedMember && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            {/* 모달 헤더 */}
            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex justify-between items-center">
              <h3 className="text-lg font-bold text-gray-900">회원 상세 정보</h3>
              <button
                onClick={() => setSelectedMember(null)}
                className="text-gray-400 hover:text-gray-600"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>

            {/* 탭 메뉴 */}
            <div className="border-b border-gray-200 px-6">
              <nav className="flex space-x-8">
                {(['info', 'attendance', 'payments', 'counseling', 'study'] as const).map((tab) => (
                  <button
                    key={tab}
                    onClick={() => setActiveTab(tab)}
                    className={`py-4 px-1 border-b-2 font-medium text-sm ${
                      activeTab === tab
                        ? 'border-blue-500 text-blue-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }`}
                  >
                    {tab === 'info' && '기본 정보'}
                    {tab === 'attendance' && '출입 기록'}
                    {tab === 'payments' && '결제 내역'}
                    {tab === 'counseling' && '상담 내역'}
                    {tab === 'study' && '학습 분석'}
                  </button>
                ))}
              </nav>
            </div>

            {/* 탭 콘텐츠 */}
            <div className="p-6">
              {/* 기본 정보 탭 */}
              {activeTab === 'info' && (
                <div className="space-y-6">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700">이름</label>
                      <p className="mt-1 text-sm text-gray-900">{selectedMember.name}</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">회원번호</label>
                      <p className="mt-1 text-sm text-gray-900">{selectedMember.user_number}</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">전화번호</label>
                      <div className="mt-1 flex items-center gap-2">
                        <p className="text-sm text-gray-900">{selectedMember.phone}</p>
                        <button
                          onClick={() => alert('데모 모드에서는 전화번호 수정이 제한됩니다.')}
                          className="text-blue-600 hover:text-blue-800"
                        >
                          <PencilIcon className="h-4 w-4" />
                        </button>
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">이메일</label>
                      <p className="mt-1 text-sm text-gray-900">{selectedMember.email}</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">생년월일</label>
                      <p className="mt-1 text-sm text-gray-900">{formatDate(selectedMember.birth_date)}</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">성별</label>
                      <p className="mt-1 text-sm text-gray-900">{selectedMember.gender}</p>
                    </div>
                    <div className="col-span-2">
                      <label className="block text-sm font-medium text-gray-700">주소</label>
                      <p className="mt-1 text-sm text-gray-900">{selectedMember.address}</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">보호자 연락처</label>
                      <p className="mt-1 text-sm text-gray-900">{selectedMember.parent_phone}</p>
                    </div>
                  </div>

                  <div className="border-t pt-6">
                    <h4 className="font-medium text-gray-900 mb-4">이용권 정보</h4>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700">이용권 종류</label>
                        <p className="mt-1 text-sm text-gray-900">
                          {getMembershipBadge(selectedMember.membership_type).label}
                        </p>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700">상태</label>
                        <p className="mt-1 text-sm text-gray-900">
                          {getStatusBadge(selectedMember.membership_status).label}
                        </p>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700">가입일</label>
                        <p className="mt-1 text-sm text-gray-900">{formatDate(selectedMember.join_date)}</p>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700">만료일</label>
                        <p className="mt-1 text-sm text-gray-900">{formatDate(selectedMember.expiry_date)}</p>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700">총 이용 시간</label>
                        <p className="mt-1 text-sm text-gray-900">{selectedMember.total_usage_hours}시간</p>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700">잔여 시간</label>
                        <p className="mt-1 text-sm text-gray-900">{selectedMember.remaining_time}시간</p>
                      </div>
                    </div>
                  </div>

                  <div className="border-t pt-6">
                    <h4 className="font-medium text-gray-900 mb-4">얼굴 인식</h4>
                    <div className="flex items-center gap-4">
                      <span className={`px-3 py-1 rounded-full text-sm font-semibold ${
                        selectedMember.face_registered ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                      }`}>
                        {selectedMember.face_registered ? '등록됨' : '미등록'}
                      </span>
                      {selectedMember.face_registered ? (
                        <button
                          onClick={() => alert('데모 모드에서는 얼굴 삭제가 제한됩니다.')}
                          className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors flex items-center gap-2"
                        >
                          <TrashIcon className="h-4 w-4" />
                          얼굴 삭제
                        </button>
                      ) : (
                        <button
                          onClick={() => alert('데모 모드에서는 얼굴 등록이 제한됩니다.')}
                          className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors flex items-center gap-2"
                        >
                          <FaceSmileIcon className="h-4 w-4" />
                          얼굴 등록
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* 출입 기록 탭 */}
              {activeTab === 'attendance' && (
                <div>
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">날짜</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">입실</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">퇴실</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">이용 시간</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {selectedMember.attendanceHistory.map((record, index) => (
                        <tr key={index}>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{record.date}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{record.entry}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{record.exit}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{record.duration}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {/* 결제 내역 탭 */}
              {activeTab === 'payments' && (
                <div>
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">날짜</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">이용권</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">금액</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">결제 방법</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">상태</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {selectedMember.paymentHistory.map((payment, index) => (
                        <tr key={index}>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{payment.date}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{payment.type}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {payment.amount.toLocaleString()}원
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{payment.method}</td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className="px-2 py-1 text-xs font-semibold rounded-full bg-green-100 text-green-800">
                              {payment.status}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {/* 상담 내역 탭 */}
              {activeTab === 'counseling' && (
                <div>
                  {selectedMember.counselingHistory.length > 0 ? (
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">날짜</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">상담자</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">분류</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">내용</th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {selectedMember.counselingHistory.map((record, index) => (
                          <tr key={index}>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{record.date}</td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{record.counselor}</td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{record.category}</td>
                            <td className="px-6 py-4 text-sm text-gray-900">{record.content}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  ) : (
                    <div className="text-center py-12">
                      <p className="text-sm text-gray-500">상담 내역이 없습니다.</p>
                    </div>
                  )}
                </div>
              )}

              {/* 학습 분석 탭 */}
              {activeTab === 'study' && (
                <div className="space-y-6">
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">조회 기간</label>
                    <input
                      type="date"
                      value={selectedDate}
                      onChange={(e) => setSelectedDate(e.target.value)}
                      className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-6">
                    <div className="bg-blue-50 rounded-lg p-6">
                      <h4 className="text-sm font-medium text-gray-700 mb-2">총 학습 일수</h4>
                      <p className="text-3xl font-bold text-blue-600">{selectedMember.studyAnalysis.totalDays}일</p>
                    </div>
                    <div className="bg-green-50 rounded-lg p-6">
                      <h4 className="text-sm font-medium text-gray-700 mb-2">총 학습 시간</h4>
                      <p className="text-3xl font-bold text-green-600">{selectedMember.studyAnalysis.totalHours}시간</p>
                    </div>
                    <div className="bg-purple-50 rounded-lg p-6">
                      <h4 className="text-sm font-medium text-gray-700 mb-2">평균 학습 시간</h4>
                      <p className="text-3xl font-bold text-purple-600">{selectedMember.studyAnalysis.avgHours}시간/일</p>
                    </div>
                    <div className="bg-orange-50 rounded-lg p-6">
                      <h4 className="text-sm font-medium text-gray-700 mb-2">최다 이용 시간대</h4>
                      <p className="text-3xl font-bold text-orange-600">{selectedMember.studyAnalysis.mostActiveTime}</p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DemoMembersPage;
